import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonSize } from '../Fundamentals/Button';
import SkynetIconButton from '../Fundamentals/IconButton/SkynetIconButton';
import type { CalculatedTier } from './PriceSimulationWorksheet.types';
import WorksheetTotal from './WorksheetTotal';
import { isFromButton } from './worksheetEvents';
import { formatMetricsPeriod } from './worksheetFormatters';

interface WorksheetTierCardProps {
    packageName: string;
    metricsPeriodStart: string | null;
    metricsPeriodEnd: string | null;
    tier: CalculatedTier;
    isCollapsed: boolean;
    onToggle: () => void;
    children: ReactNode;
}

const WorksheetTierCard = ({ packageName, metricsPeriodStart, metricsPeriodEnd, tier, isCollapsed, onToggle, children }: WorksheetTierCardProps) => {
    const { t } = useTranslation();
    const metricsPeriod = formatMetricsPeriod(metricsPeriodStart, metricsPeriodEnd);

    return (
        <div className={`rounded-8 overflow-hidden ${tier.isOutdatedVersion ? 'worksheet-tier-outdated border-top border-right border-bottom' : 'bg-default border'}`}>
            <div
                className="worksheet-tier-header cursorpointer d-flex justify-content-between align-items-center px-24 py-16 border-bottom"
                onClick={(e) => {
                    if (!isFromButton(e)) onToggle();
                }}
            >
                <div className="d-flex align-items-center gap-10">
                    <SkynetIconButton
                        icon="chevron-right"
                        iconActive="chevron-down"
                        expanded={!isCollapsed}
                        onClick={onToggle}
                        size={ButtonSize.SMALL}
                        label={t('priceSimulation.toggleSection')}
                    />
                    <span className="heading-md">
                        {packageName} &mdash; {tier.tierName}
                    </span>
                    <span className="text-secondary">{metricsPeriod ? t('priceSimulation.metricsPeriod', metricsPeriod) : t('priceSimulation.metricsPeriodMissing')}</span>
                    {tier.isOutdatedVersion && <span className="alert alert-warning alert-sm d-inline-flex mb-0">{t('priceSimulation.outdatedSourceData')}</span>}
                </div>
                <div className="d-flex align-items-center gap-8">
                    {tier.tierHasAnnualValue ? (
                        <WorksheetTotal
                            label={t('priceSimulation.tierAnnualValue')}
                            original={tier.tierCurrentAnnualValue}
                            next={tier.tierNewAnnualValue}
                            hasQuantity={tier.tierHasQuantity}
                            changeClassName="ml-4"
                        />
                    ) : (
                        <span className="body-sm text-secondary">{t('priceSimulation.noAnnualValue')}</span>
                    )}
                </div>
            </div>

            {!isCollapsed && (
                <div className="table-responsive">
                    <table className="table table-plain table-relaxed worksheet-table mb-0">
                        <colgroup>
                            <col className="col-product-plan" />
                            <col className="col-current-price" />
                            <col className="col-adjustment" />
                            <col className="col-new-price" />
                        </colgroup>
                        <thead>
                            <tr>
                                <th>{t('priceSimulation.productPlan')}</th>
                                <th className="text-right">{t('priceSimulation.currentPrice')} (SEK)</th>
                                <th className="text-center">{t('priceSimulation.adjustment')}</th>
                                <th className="text-right">{t('priceSimulation.newPrice')} (SEK)</th>
                            </tr>
                        </thead>
                        {children}
                    </table>
                </div>
            )}
        </div>
    );
};

export default WorksheetTierCard;
